import type { PronouncedGraphemes } from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.pronunciation.read";
import {
  isSequenceEntryLearned,
  type SequenceEntry,
} from "@-/dev.improve.haniverse.lib.lesson.get";
import type { Level } from "@-/dev.improve.haniverse.lib.level";

/**
 * - `unlocked`: every character of the place's name has been studied.
 * - `partial`: some, but not all, of them have.
 * - `locked`: none have.
 */
export type ChinaPlaceUnlockStatus = "locked" | "partial" | "unlocked";

export interface ChinaPlaceCharacterUnlock {
  character: string;
  /** Zero-based index of the level that teaches this character, or `null` if no level does. */
  levelIndex: number | null;
  studied: boolean;
}

export interface ChinaPlaceUnlock {
  characters: readonly ChinaPlaceCharacterUnlock[];
  /**
   * The level that unlocks the place: the latest level among its characters, since the name is
   * only readable once every character in it has been taught. `null` when some character is not
   * taught by any level.
   */
  levelIndex: number | null;
  status: ChinaPlaceUnlockStatus;
}

/** Maps each character to the first level that teaches it. */
export function createLevelIndexByCharacter(levels: readonly Level[]): ReadonlyMap<string, number> {
  const levelIndexByCharacter = new Map<string, number>();
  for (const [levelIndex, level] of levels.entries()) {
    for (const character of level.characters) {
      if (!levelIndexByCharacter.has(character)) {
        levelIndexByCharacter.set(character, levelIndex);
      }
    }
  }
  return levelIndexByCharacter;
}

/**
 * How many lessons the learner has left until each character they have not studied is taught,
 * walking the curriculum's lesson sequence in order: the character the next lesson teaches is 1
 * lesson away. Every lesson not done yet counts, pronunciation-level ones included, so this is the
 * number of lessons Learn will give them first.
 */
export function createLessonsUntilByCharacter(
  sequence: readonly SequenceEntry[],
  studiedCharacters: ReadonlySet<string>,
  pronouncedGraphemes: PronouncedGraphemes,
): ReadonlyMap<string, number> {
  const lessonsUntilByCharacter = new Map<string, number>();
  let lessonCount = 0;
  for (const entry of sequence) {
    if (
      entry.type !== "morpheme" ||
      isSequenceEntryLearned(entry, studiedCharacters, pronouncedGraphemes)
    ) {
      continue;
    }
    lessonCount += 1;
    // A pronunciation-level lesson revisits a character it does not teach.
    if (entry.part !== "pronunciation" && !lessonsUntilByCharacter.has(entry.character)) {
      lessonsUntilByCharacter.set(entry.character, lessonCount);
    }
  }
  return lessonsUntilByCharacter;
}

/**
 * How many lessons are left until a place unlocks: until its last unstudied character is taught.
 * 0 once every character is studied, `null` when some character is not taught by any lesson.
 * `unlockPlaceName` is the name in the curriculum script (see {@link getChinaPlaceUnlock}).
 */
export function getChinaPlaceLessonsUntilUnlock(
  unlockPlaceName: string,
  studiedCharacters: ReadonlySet<string>,
  lessonsUntilByCharacter: ReadonlyMap<string, number>,
): number | null {
  let lessonsUntilUnlock = 0;
  for (const character of unlockPlaceName) {
    if (studiedCharacters.has(character)) {
      continue;
    }
    const lessonsUntil = lessonsUntilByCharacter.get(character);
    if (lessonsUntil === undefined) {
      return null;
    }
    lessonsUntilUnlock = Math.max(lessonsUntilUnlock, lessonsUntil);
  }
  return lessonsUntilUnlock;
}

/**
 * Unlock progress for a place name. `placeName` is what the UI shows; when the map labels a place
 * in a different script from the learner's curriculum (Taiwan always traditional), pass
 * `unlockPlaceName` as the same name in the curriculum script so level/study lookups still match.
 */
export function getChinaPlaceUnlock(
  placeName: string,
  levelIndexByCharacter: ReadonlyMap<string, number>,
  studiedCharacters: ReadonlySet<string>,
  unlockPlaceName: string = placeName,
): ChinaPlaceUnlock {
  // oxlint-disable-next-line typescript/no-misused-spread -- Curriculum keys are Unicode scalar values, including supplementary Han characters.
  const displayCharacters = [...placeName];
  // oxlint-disable-next-line typescript/no-misused-spread -- Align the curriculum script by Unicode scalar value, matching displayCharacters.
  const unlockCharacters = [...unlockPlaceName];
  if (displayCharacters.length !== unlockCharacters.length) {
    throw new Error(
      `Unlock name length mismatch: ${placeName} (${displayCharacters.length}) vs ${unlockPlaceName} (${unlockCharacters.length})`,
    );
  }
  const characters = displayCharacters.map((character, index) => {
    const unlockCharacter = unlockCharacters[index] ?? character;
    return {
      character,
      levelIndex: levelIndexByCharacter.get(unlockCharacter) ?? null,
      studied: studiedCharacters.has(unlockCharacter),
    };
  });
  const studiedCount = characters.filter((character) => character.studied).length;
  const characterLevelIndexes = characters.map((character) => character.levelIndex);
  const levelIndex = characterLevelIndexes.includes(null)
    ? null
    : Math.max(0, ...characterLevelIndexes.filter((index) => index !== null));
  return {
    characters,
    levelIndex,
    status:
      studiedCount === characters.length ? "unlocked" : studiedCount > 0 ? "partial" : "locked",
  };
}
